import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import dashboardServices from "app/services/dashboard-services";
import {DASHBOARD_COLORS, metricChipSx, neutralChipSx} from "./chartColors";

// Las entregas cerradas de un conductor con su rango: que tan lejos del punto y que tan
// corrido de la hora planificada las cerro (Deliveries::RangeCheck). Lo abre la tabla
// "Detalle por conductor" del panel de km, con los mismos viajes que esa fila.
//
// Mismo armado que el modal de cumplimiento (OrderCompletionTrend): chips que filtran,
// busqueda en el cliente y tabla con scroll.

const FUERA = DASHBOARD_COLORS.manual;
const DENTRO = DASHBOARD_COLORS.finished;

const headerSx = {px: 1, whiteSpace: "nowrap"};
const cellSx = {px: 1, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"};

const formatDateTime = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleString("es-CL", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
};

const formatHora = (iso) => (iso
    ? new Date(iso).toLocaleTimeString("es-CL", {hour: "2-digit", minute: "2-digit"})
    : "-");

// 240 m, 3,4 km, 102 km: a partir de 10 km los decimales ya no dicen nada.
const formatDistancia = (m) => {
    if (m === null || m === undefined) return "-";
    if (m < 1000) return `${m} m`;
    const km = m / 1000;
    return `${km.toLocaleString("es-CL", {maximumFractionDigits: km < 10 ? 1 : 0})} km`;
};

// "+75 min" o "-2 h 01": con signo, porque antes y despues de lo planificado no son lo mismo.
const formatDesfase = (min) => {
    if (min === null || min === undefined) return "-";
    const signo = min > 0 ? "+" : min < 0 ? "-" : "";
    const abs = Math.abs(min);
    if (abs < 60) return `${signo}${abs} min`;
    return `${signo}${Math.floor(abs / 60)} h ${String(abs % 60).padStart(2, "0")}`;
};

const ESTADOS = {
    true: {label: "Dentro", color: DENTRO},
    false: {label: "Fuera", color: FUERA},
    null: {label: "Sin medir", color: DASHBOARD_COLORS.total}
};

const DriverRangeDetailsModal = ({open, onClose, driverId, driverName, desde, hasta}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [filtro, setFiltro] = useState(null);
    const [busqueda, setBusqueda] = useState("");

    useEffect(() => {
        if (!open) return undefined;
        let vigente = true;
        setLoading(true);
        setError("");
        setFiltro(null);
        setBusqueda("");

        dashboardServices
            .getRouteRangeDetails({desde, hasta, driver_id: driverId})
            .then((res) => { if (vigente) setData(res); })
            .catch(() => { if (vigente) setError("No fue posible cargar el detalle de entregas."); })
            .finally(() => { if (vigente) setLoading(false); });

        return () => { vigente = false; };
    }, [open, driverId, desde, hasta]);

    const filas = data?.rows ?? [];
    const cuenta = useMemo(() => ({
        dentro: filas.filter((f) => f.in_range === true).length,
        fuera: filas.filter((f) => f.in_range === false).length
    }), [filas]);

    const visibles = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        return filas.filter((f) => (
            (filtro === null || f.in_range === filtro)
            && (!q || `${f.order_code || ""} ${f.customer || ""} ${f.trip_code || ""}`.toLowerCase().includes(q))
        ));
    }, [filas, filtro, busqueda]);

    const tolerancia = data?.tolerance;
    const reglas = [
        tolerancia?.distance_m ? `hasta ${formatDistancia(tolerancia.distance_m)} del punto` : null,
        tolerancia?.time_min ? `± ${tolerancia.time_min} min de la hora planificada` : null
    ].filter(Boolean);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>{`Detalle de entregas - ${driverName || data?.driver || "Conductor"}`}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                        <Chip
                            sx={metricChipSx(DENTRO, filtro === true)}
                            label={`Dentro de rango: ${cuenta.dentro}`}
                            onClick={() => setFiltro(filtro === true ? null : true)}
                            clickable
                        />
                        <Chip
                            sx={metricChipSx(FUERA, filtro === false)}
                            label={`Fuera de rango: ${cuenta.fuera}`}
                            onClick={() => setFiltro(filtro === false ? null : false)}
                            clickable
                        />
                        <Chip
                            sx={neutralChipSx(filtro === null)}
                            label={`Total: ${filas.length}`}
                            onClick={() => setFiltro(null)}
                            clickable
                        />
                        <Typography variant="caption" color="text.secondary" sx={{pl: 1}}>
                            {reglas.length
                                ? `Rango de la compañía: ${reglas.join(" y ")}.`
                                : "La compañía no tiene rango configurado: se muestra lo medido, sin marcar dentro o fuera."}
                        </Typography>
                    </Stack>

                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Buscar por pedido, cliente o viaje"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />

                    {error && <Alert severity="error">{error}</Alert>}
                    {data?.truncated && (
                        <Alert severity="info">Se muestran las últimas 1.000 entregas del rango.</Alert>
                    )}

                    <TableContainer sx={{maxHeight: 460}}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headerSx}>Fecha/Hora cierre</TableCell>
                                    <TableCell sx={headerSx}>Pedido</TableCell>
                                    <TableCell sx={headerSx}>Cliente</TableCell>
                                    <TableCell sx={headerSx}>Viaje</TableCell>
                                    <TableCell sx={headerSx} align="right">Hora plan</TableCell>
                                    <TableCell sx={headerSx} align="right">Desfase</TableCell>
                                    <TableCell sx={headerSx} align="right">Distancia al punto</TableCell>
                                    <TableCell sx={headerSx}>Rango</TableCell>
                                    <TableCell sx={headerSx}>Motivo</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow><TableCell colSpan={9} align="center">Cargando detalle...</TableCell></TableRow>
                                ) : visibles.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">
                                            {busqueda.trim() ? "Sin resultados para la búsqueda" : "Sin entregas en el rango seleccionado"}
                                        </TableCell>
                                    </TableRow>
                                ) : visibles.map((f) => {
                                    const estado = ESTADOS[String(f.in_range)] || ESTADOS.null;
                                    return (
                                        <TableRow key={f.delivery_id}>
                                            <TableCell sx={headerSx}>{formatDateTime(f.closed_at)}</TableCell>
                                            <TableCell sx={{...cellSx, maxWidth: 100}}>{f.order_code || "-"}</TableCell>
                                            <TableCell sx={cellSx} title={f.customer || ""}>{f.customer || "-"}</TableCell>
                                            <TableCell sx={headerSx}>{f.trip_code ? `Nº ${f.trip_code.replace(/^0+/, "")}` : "-"}</TableCell>
                                            <TableCell sx={headerSx} align="right">{formatHora(f.planned_at)}</TableCell>
                                            <TableCell sx={headerSx} align="right">{formatDesfase(f.time_diff_min)}</TableCell>
                                            <TableCell sx={headerSx} align="right">
                                                {f.gps_accuracy_m ? (
                                                    <Tooltip title={`Precisión del GPS: ± ${f.gps_accuracy_m} m`}>
                                                        <span>{formatDistancia(f.distance_m)}</span>
                                                    </Tooltip>
                                                ) : formatDistancia(f.distance_m)}
                                            </TableCell>
                                            <TableCell sx={headerSx}>
                                                <Chip size="small" label={estado.label} sx={metricChipSx(estado.color, false)}/>
                                            </TableCell>
                                            <TableCell sx={{...cellSx, maxWidth: 220}} title={f.reason || ""}>{f.reason || "-"}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default DriverRangeDetailsModal;
